import i18next from 'i18next';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Mfa, MfaState } from '../interfaces';
import { mfaError } from '../../../config/commons';
import { getUserSession } from '../../../utils/userSession';
import { UserSession } from '../../../common/interfaces/auth';
import { axiosClient as axios } from '../../../utils/axios_instance';
import { openErrorNotification, openSuccessNotification } from '../../../common/notification/Notification';
import { AUTHENTICATOR_APP_URL, SETUP_AUTHENTICATOR_APP_URL, SETUP_AUTHENTICATOR_EMAIL_URL, VERIFY_AUTHENTICATOR_EMAIL_URL } from '../../../config/config.api';

const initialState: MfaState = {
  showCreateAuthenticatorAppModal: false,
  showCreateAuthenticatorEmailModal: false,
  isUpdating: false,
  isRemoving: false,
  showAuthenticatorEmailLabel: false,
  isSendingEmail: false,
};

export const mfaSlice = createSlice({
  name: 'mfa',
  initialState,
  reducers: {
    setMfas: (state: MfaState, action: PayloadAction<{mfaEmail: Mfa; mfaAuthenticator: Mfa;}>) => {
      state.mfaEmail = action.payload.mfaEmail;
      state.mfaAuthenticator = action.payload.mfaAuthenticator;
    },
    setShowCreateAuthenticatorAppModal: (state: MfaState, action: PayloadAction<boolean>) => {
      state.showCreateAuthenticatorAppModal = action.payload;
    },
    setShowCreateAuthenticatorEmailModal: (state: MfaState, action: PayloadAction<boolean>) => {
      state.showCreateAuthenticatorEmailModal = action.payload;
    },
    setQrCode: (state: MfaState, action: PayloadAction<string>) => {
      state.qrCode = action.payload;
    },
    setIsUpdating: (state: MfaState, action: PayloadAction<boolean>) => {
      state.isUpdating = action.payload;
    },
    setIsRemoving: (state: MfaState, action: PayloadAction<boolean>) => {
      state.isRemoving = action.payload;
    },
    setShowAuthenticatorEmailLabel: (state: MfaState, action: PayloadAction<boolean>) => {
      state.showAuthenticatorEmailLabel = action.payload;
    },
    setIsSendingEmail: (state: MfaState, action: PayloadAction<boolean>) => {
      state.isSendingEmail = action.payload;
    },
  },
});

export const {
  setMfas,
  setShowCreateAuthenticatorAppModal,
  setShowCreateAuthenticatorEmailModal,
  setQrCode,
  setIsUpdating,
  setIsRemoving,
  setShowAuthenticatorEmailLabel,
  setIsSendingEmail,
} = mfaSlice.actions;

export const fetchMfas = () => async (dispatch: Function) => {
  const userSession: UserSession = getUserSession();
  const url = `${AUTHENTICATOR_APP_URL}/${userSession._id}`;

  try {
    const response = await axios.get<{mfaEmail: Mfa; mfaAuthenticator: Mfa;} >(url);
    dispatch(setMfas(response.data));
  } catch (error) {
    console.log(error?.response);
  }
};

export const updateAuthenticatorApp = (props: { token: string; onSuccess: Function; }) => async (dispatch: Function) => {
  const { token, onSuccess } = props;
  
  dispatch(setIsUpdating(true));
  
  const body = {
    token,
  };

  const userSession: UserSession = getUserSession();
  const url = `${AUTHENTICATOR_APP_URL}/${userSession._id}`;

  try {
    await axios.post<Mfa[]>(url, body);
    
    dispatch(fetchMfas());
    onSuccess();
    openSuccessNotification(i18next.t('mfa.success'));
  } catch (error) {
    const errorType = error?.response?.data?.data?.error;
    showMfaError({ errorType });
  }

  dispatch(setIsUpdating(false));
};

export const generateQrCode = () => async (dispatch: Function) => {
  const userSession: UserSession = getUserSession();

  try {
    const response = await axios.post(SETUP_AUTHENTICATOR_APP_URL, { userId: userSession._id });
    dispatch(setQrCode(response.data.qrCode));
  } catch (error) {
    console.log(error.response);
  }
};

export const removeMfa = (props: { method: string; }) => async (dispatch: Function) => {
  dispatch(setIsRemoving(true));

  const userSession: UserSession = getUserSession();
  const url = `${AUTHENTICATOR_APP_URL}/${userSession._id}/${props.method}`;

  try {
    await axios.delete(url);
    dispatch(fetchMfas());
    openSuccessNotification(i18next.t('mfa.removed'));
  } catch (error) {
    console.log(error?.response);
  }

  dispatch(setIsRemoving(false));
};

export const sendMfaEmailCode = (props: {email: string;}) => async (dispatch: Function) => {
  const { email } = props;
  const userSession: UserSession = getUserSession();
  const url = `${SETUP_AUTHENTICATOR_EMAIL_URL}/${userSession._id}`;
  
  dispatch(setIsSendingEmail(true));

  try {
    await axios.post(url, { email });
    dispatch(setShowAuthenticatorEmailLabel(true));
  } catch (error) {
    console.log(error.response);
  }

  dispatch(setIsSendingEmail(false));
};

export const updateAuthenticatorEmail = (props: { code: string; onSuccess: Function; }) => async (dispatch: Function) => {
  const { code, onSuccess } = props;
  
  dispatch(setIsUpdating(true));
  
  const body = {
    code,
  };

  const userSession: UserSession = getUserSession();
  const url = `${VERIFY_AUTHENTICATOR_EMAIL_URL}/${userSession._id}`;

  try {
    await axios.post<Mfa[]>(url, body);
    
    dispatch(fetchMfas());
    onSuccess();
    openSuccessNotification(i18next.t('mfa.success'));
  } catch (error) {
    const errorType = error?.response?.data?.data?.error;
    console.log(error?.response?.data);
    showMfaError({ errorType });
  }

  dispatch(setIsUpdating(false));
};

export const showMfaError = (props: {errorType: string}) => {
  const { errorType } = props;

  switch (errorType) {
    case mfaError.INVALID_TOKEN:
    case mfaError.INVALID_CODE:
      openErrorNotification(i18next.t('mfa.invalidToken'));
      break;

    case mfaError.CODE_EXPIRED:
      openErrorNotification(i18next.t('mfa.codeExpired'));
      break;

    default:
      openErrorNotification(i18next.t('mfa.error'));
      break;
  }
};

export default mfaSlice.reducer;
